// HRM_CLEANUP: Delete this entire file and HrmDocumentShareConfigurationControl.module.scss when Ontario MD certification is complete
import { ReactElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Client } from "@/Common/Api/Client";
import { Modal } from "@/Common/Components/Modals/Modal";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import * as operations from "@/Common/Operations/operations.server";
import { Button, Tag } from "@/DesignSystem";

import styles from "./HrmDocumentShareConfigurationControl.module.scss";

function SharedWithMe(): ReactElement {
    const [sharedWithMe, setSharedWithMe] = useState<Person[]>([]);
    const getSharesAsync = async (): Promise<Guid[]> => {
        const response = await Client.postAsync<{ HrmDocumentShares: Guid[] }>(
            operations.Server.Hrm_GetHrmSharesWithMe,
            null
        );

        return response.data.HrmDocumentShares;
    };

    useEffect(
        () => {
            const load = async (): Promise<void> => {
                const hrmDocumentShares = await getSharesAsync();
                setSharedWithMe(cacheStore.GetClinicians(false).filter(p => hrmDocumentShares.contains(p.ContactId)));
            };

            load();
        },
        []
    );

    const hasSharedWithMe = sharedWithMe.length > 0;

    return (
        <div>
            {!hasSharedWithMe && <h1>Looks like no colleagues are sharing with you right now.</h1>}
            {hasSharedWithMe && (
                <div className={styles.completionAssistTags}>
                    <h1>The following people are sharing their documents with you.</h1>
                    {sharedWithMe.map(clinician => (
                        <div key={clinician.ContactId} className={styles.clinicianTag}>
                            <Tag
                                label={clinician.NameWithPrefix}
                                size="md"
                                theme="light"
                                variant="neutral"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
interface ICliniciansSelectorProps {
    onChange: (selection: Person[]) => void;
    currentSelection: Person[];
}

function CliniciansSelector(props: Readonly<ICliniciansSelectorProps>): ReactElement {
    const [selection, setSelection] = useState<Person[]>(props.currentSelection ?? []);
    const [searchText, setSearchText] = useState<string>("");
    const [completionAssistValues, setCompletionAssistValues] = useState<Person[]>([]);

    const cliniciansWithoutCurrentUser = useRef(
        cacheStore.GetClinicians(false).filter(p => p.ContactFacetId != sessionStore.getCurrentEmployee().ContactFacetId)
    );

    const selectClinician = (clinician: Person, event: React.MouseEvent<HTMLDivElement>): void => {
        setSelection([...selection, clinician].sortByProperty(p => p.NameWithPrefix));
        if (!event.shiftKey)
            setSearchText("");
    };

    const deselectClinician = (clinician: Person): void => {
        setSelection(selection.filter(p => p !== clinician));
    };

    useEffect(
        () => {
            if (searchText) {
                setCompletionAssistValues(
                    cliniciansWithoutCurrentUser.current
                        .filter(p => p.NameWithPrefix.contains(searchText, { ignoreCase: true, ignoreDiacritics: true }))
                        .filter(p => !selection.contains(p))
                );
            }
            else { setCompletionAssistValues([]); }
        },
        [searchText, selection]
    );

    useEffect(
        () => props.onChange(selection),
        [selection, props]
    );

    const hasShares = selection.length > 0;

    return (
        <React.Fragment>
            <div>
                <SharedWithMe />
                <h1>Search and select clinicians to share your documents with.</h1>

                <input
                    id="clinician-search"
                    name="clinician"
                    autoComplete="off"
                    placeholder="Search clinicians..."
                    onChange={event => setSearchText(event.target.value)}
                    value={searchText}
                />

                <div className={styles.completionAssistTags}>
                    {completionAssistValues.map(clinician => (
                        <div key={clinician.ContactId} onClick={event => selectClinician(clinician, event)} className={styles.clinicianTag}>
                            <Tag
                                label={clinician.NameWithPrefix}
                                size="md"
                                theme="light"
                                variant="neutral"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {!hasShares && (<h1>You are not sharing your documents with anyone.</h1>
            )}
            {hasShares && (
                <div>
                    <h1>You are currently sharing your documents with.</h1>
                    <div className={styles.shares}>
                        {selection.map(clinician => (
                            <div key={clinician.ContactId} onClick={() => deselectClinician(clinician)} className={styles.clinicianTag}>
                                <Tag
                                    label={clinician.NameWithPrefix}
                                    size="md"
                                    theme="light"
                                    variant="neutral"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}
interface IHrmDocumentSharePickerProps {
    onChange: (selection: Person[]) => void;
    currentSelection: Person[];
}

function HrmDocumentSharePicker(props: Readonly<IHrmDocumentSharePickerProps>): ReactElement {
    return (
        <div className={styles.hrmDocumentShareConfigurationControl}>
            <CliniciansSelector onChange={p => props.onChange(p)} currentSelection={props.currentSelection} />
        </div>
    );
}

interface IHrmDocumentSharePickerFooterProps {
    onSaveClick: () => void;
    onCancelClick: () => void;
}

function HrmDocumentSharePickerFooter(props: Readonly<IHrmDocumentSharePickerFooterProps>): ReactElement {
    return (
        <div className={styles.footer}>
            <Button label="Cancel" onClick={props.onCancelClick} category="secondary" />
            <Button label="Save" onClick={props.onSaveClick} />
        </div>
    );
}

function HrmDocumentShareConfigurationPopup(): ReactElement {
    const [showPopup, setShowPopup] = useState(false);
    const [selectedClinicians, setSelectedClinicians] = useState<Person[]>([]);

    const saveToServer = (selectedClinicians: Person[]): void => {
        Client.postAsync(
            operations.Server.Hrm_SaveMyHrmDocumentShares,
            {
                selectedClinicianContactIds: selectedClinicians.map(p => p.ContactId)
            }
        );

        setShowPopup(false);
    };

    const getSharesAsync = async (): Promise<Guid[]> => {
        const response = await Client.postAsync<{ HrmDocumentShares: Guid[] }>(
            operations.Server.Hrm_GetMyHrmDocumentShares,
            null
        );

        return response.data.HrmDocumentShares;
    };

    const closePopup = (): void => {
        setShowPopup(false);
    };

    const loadInitialSharesAndShowPopupAsync = async (): Promise<void> => {
        const hrmDocumentShares = await getSharesAsync();
        const selectedClinicians = cacheStore.GetClinicians(false).filter(p => hrmDocumentShares.contains(p.ContactId));
        setSelectedClinicians(selectedClinicians);
        setShowPopup(true);
    };

    const popup = createPortal(
        <Modal
            dataCy="dhdr-popup"
            showOverlay={true}
            top={20}
            onClose={closePopup}
            showX
            title="Hrm Document Sharing Configuration"
            cssClass={styles.hrmDocumentShareConfigurationControlPopup}
            footer={(
                <HrmDocumentSharePickerFooter
                    onSaveClick={() => saveToServer(selectedClinicians)}
                    onCancelClick={closePopup}
                />
            )}
        >
            <HrmDocumentSharePicker onChange={p => setSelectedClinicians(p)} currentSelection={selectedClinicians} />
        </Modal>,
        document.body
    );

    return (
        <React.Fragment>
            {showPopup && popup}
            <div className="row">
                <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                    <div className="form-label bold">HRM sharing</div>
                </div>
                <div className="col-sm-12 col-md-10 col-lg-10">
                    <div className="row native">
                        <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                            <div className="visible-xs bold visible-sm">HRM sharing</div>
                            <div>
                                <Button
                                    label="Configure HRM Document Sharing"
                                    onClick={loadInitialSharesAndShowPopupAsync}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export { HrmDocumentShareConfigurationPopup };
